import * as Crypto from "effect/Crypto";
import { Atom } from "effect/unstable/reactivity";

import { createAtomCommandScheduler, createEnvironmentCommand } from "./runtime.ts";
import {
  type ManageThreadInput,
  type PinThreadInput,
  type ReorderActiveThreadInput,
  type ReorderPinnedThreadInput,
  type UnpinThreadInput,
  manageThread,
  pinThread,
  reorderActiveThread,
  reorderPinnedThread,
  unpinThread,
  type ArchiveThreadInput,
  type CreateThreadInput,
  type ForkThreadInput,
  type DeleteThreadInput,
  type ControlGoalInput,
  type CompactThreadInput,
  type InterruptThreadTurnInput,
  type RespondToThreadApprovalInput,
  type RespondToThreadUserInputInput,
  type RevertThreadCheckpointInput,
  type SetThreadInteractionModeInput,
  type SetThreadRuntimeModeInput,
  type StartThreadTurnInput,
  type StopThreadSessionInput,
  type WarmThreadSessionInput,
  type UnarchiveThreadInput,
  type UpdateThreadMetadataInput,
  type LinkThreadPullRequestInput,
  type UnlinkThreadPullRequestInput,
  archiveThread,
  createThread,
  forkThread,
  deleteThread,
  controlGoal,
  compactThread,
  interruptThreadTurn,
  respondToThreadApproval,
  respondToThreadUserInput,
  revertThreadCheckpoint,
  setThreadInteractionMode,
  setThreadRuntimeMode,
  startThreadTurn,
  stopThreadSession,
  warmThreadSession,
  unarchiveThread,
  updateThreadMetadata,
  linkThreadPullRequest,
  unlinkThreadPullRequest,
} from "../operations/commands.ts";
import type { EnvironmentRegistry } from "../connection/registry.ts";

export type {
  ArchiveThreadInput,
  CreateThreadInput,
  ForkThreadInput,
  DeleteThreadInput,
  InterruptThreadTurnInput,
  RespondToThreadApprovalInput,
  RespondToThreadUserInputInput,
  RevertThreadCheckpointInput,
  SetThreadInteractionModeInput,
  SetThreadRuntimeModeInput,
  StartThreadTurnInput,
  StopThreadSessionInput,
  UnarchiveThreadInput,
  UpdateThreadMetadataInput,
  LinkThreadPullRequestInput,
  UnlinkThreadPullRequestInput,
} from "../operations/commands.ts";

export function createThreadEnvironmentAtoms<R, E>(
  runtime: Atom.AtomRuntime<EnvironmentRegistry | Crypto.Crypto | R, E>,
) {
  const scheduler = createAtomCommandScheduler();
  const concurrency = {
    mode: "serial" as const,
    key: ({
      environmentId,
      input,
    }: {
      environmentId: string;
      input: { threadId?: string; sourceThreadId?: string; targetThreadId?: string };
    }) =>
      JSON.stringify([
        environmentId,
        input.threadId ?? input.sourceThreadId ?? input.targetThreadId,
      ]),
  };
  return {
    create: createEnvironmentCommand(runtime, {
      label: "environment-data:commands:thread:create",
      surviveReadinessChanges: true,
      execute: (input: CreateThreadInput) => createThread(input),
      scheduler,
      concurrency,
    }),
    fork: createEnvironmentCommand(runtime, {
      label: "environment-data:commands:thread:fork",
      surviveReadinessChanges: true,
      execute: (input: ForkThreadInput) => forkThread(input),
      scheduler,
      concurrency,
    }),
    delete: createEnvironmentCommand(runtime, {
      label: "environment-data:commands:thread:delete",
      surviveReadinessChanges: true,
      execute: (input: DeleteThreadInput) => deleteThread(input),
      scheduler,
      concurrency,
    }),
    manage: createEnvironmentCommand(runtime, {
      label: "environment-data:commands:thread:manage",
      surviveReadinessChanges: true,
      execute: (input: ManageThreadInput) => manageThread(input),
      scheduler,
      concurrency,
    }),
    pin: createEnvironmentCommand(runtime, {
      label: "environment-data:commands:thread:pin",
      surviveReadinessChanges: true,
      execute: (input: PinThreadInput) => pinThread(input),
      scheduler,
      concurrency,
    }),
    unpin: createEnvironmentCommand(runtime, {
      label: "environment-data:commands:thread:unpin",
      surviveReadinessChanges: true,
      execute: (input: UnpinThreadInput) => unpinThread(input),
      scheduler,
      concurrency,
    }),
    reorderPin: createEnvironmentCommand(runtime, {
      label: "environment-data:commands:thread:reorder-pin",
      surviveReadinessChanges: true,
      execute: (input: ReorderPinnedThreadInput) => reorderPinnedThread(input),
      scheduler,
      concurrency,
    }),
    reorderActive: createEnvironmentCommand(runtime, {
      label: "environment-data:commands:thread:reorder-active",
      surviveReadinessChanges: true,
      execute: (input: ReorderActiveThreadInput) => reorderActiveThread(input),
      scheduler,
      concurrency,
    }),
    archive: createEnvironmentCommand(runtime, {
      label: "environment-data:commands:thread:archive",
      surviveReadinessChanges: true,
      execute: (input: ArchiveThreadInput) => archiveThread(input),
      scheduler,
      concurrency,
    }),
    linkPullRequest: createEnvironmentCommand(runtime, {
      label: "environment-data:commands:thread:link-pull-request",
      surviveReadinessChanges: true,
      execute: (input: LinkThreadPullRequestInput) => linkThreadPullRequest(input),
      scheduler,
      concurrency,
    }),
    unlinkPullRequest: createEnvironmentCommand(runtime, {
      label: "environment-data:commands:thread:unlink-pull-request",
      surviveReadinessChanges: true,
      execute: (input: UnlinkThreadPullRequestInput) => unlinkThreadPullRequest(input),
      scheduler,
      concurrency,
    }),
    unarchive: createEnvironmentCommand(runtime, {
      label: "environment-data:commands:thread:unarchive",
      surviveReadinessChanges: true,
      execute: (input: UnarchiveThreadInput) => unarchiveThread(input),
      scheduler,
      concurrency,
    }),
    updateMetadata: createEnvironmentCommand(runtime, {
      label: "environment-data:commands:thread:update-metadata",
      surviveReadinessChanges: true,
      execute: (input: UpdateThreadMetadataInput) => updateThreadMetadata(input),
      scheduler,
      concurrency,
    }),
    setRuntimeMode: createEnvironmentCommand(runtime, {
      label: "environment-data:commands:thread:set-runtime-mode",
      surviveReadinessChanges: true,
      execute: (input: SetThreadRuntimeModeInput) => setThreadRuntimeMode(input),
      scheduler,
      concurrency,
    }),
    setInteractionMode: createEnvironmentCommand(runtime, {
      label: "environment-data:commands:thread:set-interaction-mode",
      surviveReadinessChanges: true,
      execute: (input: SetThreadInteractionModeInput) => setThreadInteractionMode(input),
      scheduler,
      concurrency,
    }),
    startTurn: createEnvironmentCommand(runtime, {
      label: "environment-data:commands:thread:start-turn",
      execute: (input: StartThreadTurnInput) => startThreadTurn(input),
      surviveReadinessChanges: true,
      // Stable command/message identities make concurrent transport dispatch safe.
      // Do not hold the shared mutation lane while waiting for an RPC response:
      // a lost response must not starve a later busy-turn enqueue.
    }),
    controlGoal: createEnvironmentCommand(runtime, {
      label: "environment-data:commands:thread:goal",
      surviveReadinessChanges: true,
      execute: (input: ControlGoalInput) => controlGoal(input),
    }),
    compact: createEnvironmentCommand(runtime, {
      label: "environment-data:commands:thread:compact",
      execute: (input: CompactThreadInput) => compactThread(input),
    }),
    interruptTurn: createEnvironmentCommand(runtime, {
      label: "environment-data:commands:thread:interrupt-turn",
      surviveReadinessChanges: true,
      execute: (input: InterruptThreadTurnInput) => interruptThreadTurn(input),
    }),
    respondToApproval: createEnvironmentCommand(runtime, {
      label: "environment-data:commands:thread:respond-to-approval",
      surviveReadinessChanges: true,
      execute: (input: RespondToThreadApprovalInput) => respondToThreadApproval(input),
      scheduler,
      concurrency,
    }),
    respondToUserInput: createEnvironmentCommand(runtime, {
      label: "environment-data:commands:thread:respond-to-user-input",
      surviveReadinessChanges: true,
      execute: (input: RespondToThreadUserInputInput) => respondToThreadUserInput(input),
      scheduler,
      concurrency,
    }),
    revertCheckpoint: createEnvironmentCommand(runtime, {
      label: "environment-data:commands:thread:revert-checkpoint",
      surviveReadinessChanges: true,
      execute: (input: RevertThreadCheckpointInput) => revertThreadCheckpoint(input),
      scheduler,
      concurrency,
    }),
    stopSession: createEnvironmentCommand(runtime, {
      label: "environment-data:commands:thread:stop-session",
      surviveReadinessChanges: true,
      execute: (input: StopThreadSessionInput) => stopThreadSession(input),
    }),
    warmSession: createEnvironmentCommand(runtime, {
      label: "environment-data:commands:thread:warm-session",
      surviveReadinessChanges: true,
      execute: (input: WarmThreadSessionInput) => warmThreadSession(input),
    }),
  };
}
